/* IndexedDB offline store for TrailDog.
 *
 * Only stores routes the user has explicitly "Saved offline" — no auto-caching.
 *
 * ToS compliance (Google Maps Platform):
 *   • Never persist live transit schedules, step-by-step instructions,
 *     or Google Places metadata beyond a 30-day session.
 *   • We DO persist only:
 *       - our own trail metadata (name, region, dog amenities we curate)
 *       - lat/lng of trailhead (which is our own data, not a Google Place)
 *       - polyline (OSM/OS data — our source, not Google)
 *       - weather snapshot (OpenWeather — not Google)
 *       - a pre-composed Google Maps DEEP LINK URL (a string, no schedule data)
 *
 *   The deep link, when tapped offline, hands off to the user's native Google
 *   Maps app so live transit routing is resolved by Google directly, not us.
 */
const DB_NAME = "traildog-offline";
const DB_VERSION = 1;
const STORE_ROUTES = "saved_routes";

let _dbPromise = null;

function open() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_ROUTES)) {
        db.createObjectStore(STORE_ROUTES, { keyPath: "location_id" });
      }
    };
  });
  return _dbPromise;
}

async function txStore(mode = "readonly") {
  const db = await open();
  return db.transaction(STORE_ROUTES, mode).objectStore(STORE_ROUTES);
}

/** Build the ToS-safe Google Maps deep link for the destination.
 *  Uses lat/lng only (no place metadata) — Google Maps resolves the user's
 *  origin natively when the link is tapped on-device.
 */
export function buildGoogleTransitDeepLink({ lat, lng, name }) {
  const dest = `${lat},${lng}`;
  const params = new URLSearchParams({ api: "1", destination: dest, travelmode: "transit" });
  if (name) params.set("destination_place", name);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Also expose a plain "search" deep link (no origin) as a fallback. */
export function buildGoogleSearchDeepLink({ lat, lng }) {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/**
 * Persist one route to IndexedDB.
 *
 * @param {object} route  Trail JSON from GET /api/locations/{id}
 * @param {object} extras Optional { weather, dog_amenities }
 */
export async function putSavedRoute(route, extras = {}) {
  if (!route || !route.location_id) throw new Error("route.location_id required");
  const store = await txStore("readwrite");
  const record = {
    location_id: route.location_id,
    name: route.name,
    region: route.region || "",
    type: route.type || "hiking",
    lat: route.lat,
    lng: route.lng,
    polyline: route.polyline || [],       // OSM/OS data, safe to persist
    dog_amenities: route.dog_amenities || [],
    short_desc: route.short_desc || "",
    // For basecamps: preserve the amenity / energy / lead / transit fields so
    // the Favourites tab and offline detail view can render the correct
    // basecamp UI instead of falling back to a generic trail card.
    basecamp: route.type === "basecamp" ? {
      basecamp_id: route.basecamp_id || String(route.location_id).replace(/^bc:/, ""),
      has_water_tap: !!route.has_water_tap,
      dog_waste_bins: !!route.dog_waste_bins,
      cafe_on_site: !!route.cafe_on_site,
      has_toilets: !!route.has_toilets,
      has_car_park: !!route.has_car_park,
      has_shop: !!route.has_shop,
      pub_nearby: !!route.pub_nearby,
      picnic_tables: !!route.picnic_tables,
      bbq_allowed: !!route.bbq_allowed,
      dog_wash_station: !!route.dog_wash_station,
      lead_status: route.lead_status || "strictly_on_lead",
      energy_match: route.energy_match || "chill",
      bus_distance_meters: route.bus_distance_meters ?? null,
      bus_path_quality: route.bus_path_quality || "",
    } : undefined,
    // ToS-safe deep links (strings only, no schedule data)
    google_transit_deep_link: buildGoogleTransitDeepLink({
      lat: route.lat, lng: route.lng, name: route.name,
    }),
    google_search_deep_link: buildGoogleSearchDeepLink({ lat: route.lat, lng: route.lng }),
    // Snapshot data
    weather_snapshot: extras.weather || null,
    saved_at: new Date().toISOString(),
  };
  return new Promise((resolve, reject) => {
    const req = store.put(record);
    req.onsuccess = () => resolve(record);
    req.onerror = () => reject(req.error);
  });
}

export async function getSavedRoute(locationId) {
  const store = await txStore("readonly");
  return new Promise((resolve, reject) => {
    const req = store.get(locationId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function listSavedRoutes() {
  const store = await txStore("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteSavedRoute(locationId) {
  const store = await txStore("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.delete(locationId);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

export async function hasSavedRoute(locationId) {
  const r = await getSavedRoute(locationId);
  return !!r;
}
